/**
 * components/riskTerminal/marginModel.js
 *
 * upstox-086 — the margin footer / phone line, from the panel's `margin` block (services/upstox_broker/margin.py).
 * Every figure is the BROKER's field as served; nothing here adds or subtracts them. The gauge is
 * USED ÷ (AVAILABLE + USED) in 20 steps; a negative USED reads IN CREDIT with a hollow 21st step; under 5 % available is
 * amber "NEARLY FULLY USED"; AVAILABLE < 0 is red "SHORT · ADD FUNDS". A missing figure is "—", never ₹0.
 */

import { hhmm } from './theme';

export const STEPS = 20;
export const LOW_FRAC = 0.05;

/** ₹ with Indian grouping and a real minus; null → "—". */
export function rupees(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return (n < 0 ? '−' : '') + '₹' + Math.round(Math.abs(n)).toLocaleString('en-IN');
}

/** The gauge and its readout for one segment ({available, used}); null when the two figures can't make a fraction. */
export function gaugeOf(seg) {
  if (!seg || seg.available == null || seg.used == null) return null;
  const funds = seg.available + seg.used;
  const short = seg.available < 0;
  const credit = seg.used < 0;
  if (!(funds > 0) && !short) return { steps: STEPS, filled: 0, tone: 'grey', credit, short, low: false, readout: credit ? 'IN CREDIT' : '', readTone: 'grey' };
  const frac = funds > 0 ? Math.max(0, Math.min(1, seg.used / funds)) : 1;
  const low = !short && funds > 0 && seg.available / funds < LOW_FRAC;
  const tone = short ? 'red' : low ? 'amber' : 'grey';
  const readout = short ? 'SHORT · ADD FUNDS' : credit ? 'IN CREDIT' : low ? 'NEARLY FULLY USED' : Math.round(frac * 100) + '% USED';
  return { steps: STEPS, filled: short ? STEPS : Math.round(frac * STEPS), tone, credit, short, low, readout, readTone: short ? 'red' : low ? 'amber' : 'grey' };
}

/** Seconds since the read, from the panel's own clock (`now` ms); null when never read. */
export function ageOf(block, nowMs) {
  const t = block && block.read_at ? Date.parse(block.read_at) : NaN;
  return Number.isFinite(t) && nowMs ? Math.max(0, Math.round((nowMs - t) / 1000)) : null;
}

/**
 * What the footer / line shows.
 *   state: ok | pending | not_signed_in | unavailable | error | none (a panel with no margin block)
 *   seg: the equity segment's figures (also on the failure states: the last good read) · mcx: the commodity segment or null
 *   old: an ok read older than the block's `old_after_s` (5 min) · ageMin · lastRead "hh:mm"
 */
export function marginView(block, nowMs) {
  if (!block) return { state: 'none' };
  const fig = block.figures || null;
  const seg = fig && fig.equity ? fig.equity : null;
  const age = ageOf(block, nowMs);
  const oldAfter = block.old_after_s || 300;
  const lastRead = block.read_at ? hhmm(block.read_at) : '';
  return {
    state: block.state || 'pending', broker: block.broker, message: block.message || '', labels: block.labels || {},
    seg, mcx: fig ? fig.commodity || null : null, gauge: gaugeOf(seg), mcxGauge: gaugeOf(fig && fig.commodity),
    lastRead, age, ageMin: age == null ? null : Math.floor(age / 60),
    old: block.state === 'ok' && age != null && age >= oldAfter,
    hasBracket: !!seg && (seg.collateral != null || seg.cash != null),
  };
}
